import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import { defaultExpertAppSubdomain } from '../lib/utils';
import TenantProductsField, { APP_SUBDOMAIN_TAKEN_MESSAGE } from '../components/TenantProductsField';

const CreateTenant: React.FC = () => {
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [tenantType, setTenantType] = useState('product');
  const [clientAdminEnabled, setClientAdminEnabled] = useState(true);
  const [domainExpertEnabled, setDomainExpertEnabled] = useState(false);
  const [appSubdomain, setAppSubdomain] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createTenantMutation = useMutation({
    mutationFn: apiClient.createTenant,
    onSuccess: () => {
      // Invalidate and refetch tenants list
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate('/tenants');
    },
    onError: (error: Error) => {
      console.error('Error creating tenant:', error);
    },
  });

  // Clean subdomain (remove spaces, lowercase, etc.)
  const cleanSubdomain = subdomain.toLowerCase().trim().replace(/\s+/g, '-');
  const appSubdomainValue = appSubdomain ?? (cleanSubdomain ? defaultExpertAppSubdomain(cleanSubdomain) : '');
  const appSubdomainTaken = (createTenantMutation.error as ApiError | null)?.code === 'app_subdomain_taken';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !subdomain) {
      return;
    }

    createTenantMutation.mutate({ 
      name: name.trim(), 
      subdomain: cleanSubdomain,
      tenant_type: tenantType,
      client_admin_enabled: clientAdminEnabled,
      domain_expert_enabled: domainExpertEnabled,
      ...(domainExpertEnabled ? { app_subdomain: appSubdomainValue } : {}),
    });
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Create New Tenant</h1>
      {createTenantMutation.error && !appSubdomainTaken && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          {(createTenantMutation.error as Error).message}
        </div>
      )}
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Tenant Name
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="name"
            type="text"
            placeholder="Tenant Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={createTenantMutation.isLoading}
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subdomain">
            Subdomain
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
            id="subdomain"
            type="text"
            placeholder="subdomain"
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value)}
            disabled={createTenantMutation.isLoading}
          />
           <p className="text-xs italic">This will be used for the tenant's unique URL (e.g., subdomain.yourapp.com)</p>
        </div>
        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="tenant-type">
            Business Type
          </label>
          <select
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="tenant-type"
            value={tenantType}
            onChange={(e) => setTenantType(e.target.value)}
            disabled={createTenantMutation.isLoading}
          >
            <option value="product">Product Company</option>
            <option value="service">Service Company</option>
          </select>
          <p className="text-xs italic">
            Choose "Product" for companies selling physical/digital products, "Service" for companies providing services
          </p>
        </div>
        <div className="mb-6">
          <TenantProductsField
            clientAdminEnabled={clientAdminEnabled}
            domainExpertEnabled={domainExpertEnabled}
            onClientAdminChange={setClientAdminEnabled}
            onDomainExpertChange={setDomainExpertEnabled}
            appSubdomain={appSubdomainValue}
            onAppSubdomainChange={(value) => {
              setAppSubdomain(value);
              if (appSubdomainTaken) createTenantMutation.reset();
            }}
            appSubdomainError={appSubdomainTaken ? APP_SUBDOMAIN_TAKEN_MESSAGE : null}
            appSubdomainHint="Where users sign in. Defaults to the subdomain followed by -ea."
            disabled={createTenantMutation.isLoading}
          />
        </div>
        <div className="flex items-center justify-between">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
            type="submit"
            disabled={
              createTenantMutation.isLoading ||
              !name.trim() ||
              !subdomain.trim() ||
              (domainExpertEnabled && !appSubdomainValue)
            }
          >
            {createTenantMutation.isLoading ? 'Creating...' : 'Create Tenant'}
          </button>
          <button
            className="inline-block align-baseline font-bold text-sm text-blue-500 hover:text-blue-800"
            type="button"
            onClick={() => navigate('/tenants')}
            disabled={createTenantMutation.isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTenant;
